import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Banner = styled.aside`
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  width: min(92vw, 640px);
  background: rgba(7, 12, 20, 0.92);
  border: 1px solid rgba(92, 233, 255, 0.18);
  border-radius: 28px;
  padding: 1rem;
  box-shadow: 0 28px 60px rgba(1, 6, 19, 0.55);
  color: rgba(226, 232, 240, 0.9);
  backdrop-filter: blur(18px);
  z-index: 1200;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;

  @media (max-width: 640px) {
    padding: 1.5rem 1.2rem 1.75rem;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const GlowDot = styled.span`
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #5ce9ff, #6e72fc);
  box-shadow: 0 0 16px rgba(92, 233, 255, 0.7);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 800;
  letter-spacing: 0.01em;
  background: linear-gradient(90deg, #00f7d2, #5ce9ff);
  -webkit-background-clip: text;
  color: transparent;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 0.65rem 1.4rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
  border: 1px solid transparent;
`;

const EssentialButton = styled(ActionButton)`
  background: rgba(14, 21, 38, 0.75);
  border-color: rgba(92, 233, 255, 0.3);
  color: rgba(226, 232, 240, 0.82);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 28px rgba(91, 151, 255, 0.24);
  }
`;

const AcceptButton = styled(ActionButton)`
  background: linear-gradient(135deg, #6e72fc, #00f7d2);
  color: #001018;
  box-shadow: 0 18px 32px rgba(94, 115, 255, 0.38);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 22px 40px rgba(94, 115, 255, 0.45);
  }
`;

const Description = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.6;
  color: rgba(203, 213, 225, 0.92);

  strong {
    color: #ffffff;
  }
`;

const LearnMoreLink = styled(Link)`
  color: #5ce9ff;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const FinePrint = styled.div`
  font-size: 0.78rem;
  color: rgba(148, 163, 184, 0.9);
`;

const CookieBanner = ({ visible, onAccept, onDismiss }) => {
  if (!visible) return null;

  return (
    <Banner role="dialog" aria-live="polite">
      <HeaderRow>
        <TitleBlock>
          <GlowDot aria-hidden />
          <Title>Cookie preferences</Title>
        </TitleBlock>
        <Actions>
          <EssentialButton type="button" onClick={onDismiss}>
            Only essential
          </EssentialButton>
          <AcceptButton type="button" onClick={onAccept}>
            Accept all
          </AcceptButton>
        </Actions>
      </HeaderRow>
      <Description>
        We use essential cookies and, with your consent, analytics cookies to improve your experience on{' '}
        <strong>Custodiy</strong>. You can change your choice at any time using the “Manage cookies” link in the
        footer.{' '}
        <LearnMoreLink to="/privacy">
          Learn more
        </LearnMoreLink>
      </Description>
      <FinePrint>We never sell your data to third parties.</FinePrint>
    </Banner>
  );
};

export default CookieBanner;

